import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

const { createContext, useContext, useState } = require('react')

export const SearchSalesDocumentsContext = createContext()

const BASE_FILTERS = {
  pageNumber: 1,
  pageSize: 10,
  companyId: null,
  warehouseId: null,
}

function buildInitialFilters(initialFilters, companyId, warehouseId) {
  return {
    ...BASE_FILTERS,
    ...(initialFilters || {}),
    companyId,
    warehouseId,
  }
}

export function SearchSalesDocumentsProvider({
  children,
  fetcher,
  initialFilters,
  buildPayload,
  enabled = true,
}) {
  if (typeof fetcher !== 'function') {
    throw new Error(
      'SearchSalesDocumentsProvider requires a valid "fetcher" function'
    )
  }
  if (typeof buildPayload !== 'function') {
    throw new Error(
      'SearchSalesDocumentsProvider requires a valid "buildPayload" function'
    )
  }

  const companyId = useSelector(state => state.company.CompanyId)
  const warehouseId = useSelector(state => state.company.WarehouseId)

  const [dataDocuments, setDataDocuments] = useState([])
  const [loadingData, setLoadingData] = useState(true)
  const [errorFormat, setErrorFormat] = useState(false)
  const [isMoreData, setIsMoreData] = useState(true)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageWindowFirst, setPageWindowFirst] = useState(0)
  const [pageWindowLast, setPageWindowLast] = useState(3)
  const requestIdRef = useRef(0)
  const resolvedInitialFilters = useMemo(
    () => initialFilters || {},
    [initialFilters]
  )
  const [filters, setFilters] = useState(() =>
    buildInitialFilters(resolvedInitialFilters, companyId, warehouseId)
  )

  const updateFilter = useCallback((key, value, type) => {
    if (key === 'companyId') return

    setFilters(prev => {
      let newPageNumber = prev.pageNumber
      if ((key !== 'pageNumber' && type === 'filter') || key === 'pageSize') {
        newPageNumber = 1
      } else if (key === 'pageNumber') {
        newPageNumber = value
      }

      const newValue = {
        ...prev,
        [key]: value,
        pageNumber: newPageNumber,
      }

      if (JSON.stringify(prev) === JSON.stringify(newValue)) return prev
      return newValue
    })
  }, [])

  const getDataDocuments = useCallback(
    async props => {
      if (!enabled) return null
      const payload = buildPayload(props)
      const response = await fetcher(payload)
      return response?.data
    },
    [buildPayload, fetcher, enabled]
  )

  const setSearchResultState = useCallback(data => {
    const firstItem = data?.Data?.[0]
    setDataDocuments(data?.Data || [])
    setIsMoreData(firstItem?.HasNextPage ?? false)
    setTotalPages(firstItem?.TotalPages ?? 0)
    setTotalRecords(firstItem?.TotalRegisters ?? 0)
  }, [])

  const searchDocuments = useCallback(
    async (filtersProps, options = {}) => {
      const { syncFilters = false } = options
      const requestId = ++requestIdRef.current
      if (syncFilters) setFilters(filtersProps)
      setLoadingData(true)

      // Keep the shared loading state until the required search data is ready.
      if (!enabled) {
        setSearchResultState(null)
        return null
      }

      try {
        const data = await getDataDocuments(filtersProps)
        if (requestId !== requestIdRef.current) return null
        setSearchResultState(data)
        return data
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error('Error on searchDocuments:', error)
          setDataDocuments([])
          setIsMoreData(false)
          setTotalPages(0)
          setTotalRecords(0)
        }
        return null
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingData(false)
        }
      }
    },
    [getDataDocuments, setSearchResultState, enabled]
  )

  const applyFilters = useCallback(
    async filtersProps => {
      await searchDocuments(filtersProps, { syncFilters: true })
    },
    [searchDocuments]
  )

  const clearFilters = useCallback(async () => {
    const resetFilters = buildInitialFilters(
      resolvedInitialFilters,
      companyId,
      warehouseId
    )
    await searchDocuments(resetFilters, { syncFilters: true })
  }, [resolvedInitialFilters, companyId, warehouseId, searchDocuments])

  useEffect(() => {
    const initializeData = async () => {
      const newFilters = buildInitialFilters(
        resolvedInitialFilters,
        companyId,
        warehouseId
      )
      await searchDocuments(newFilters, { syncFilters: true })
    }

    initializeData()
  }, [resolvedInitialFilters, companyId, warehouseId, searchDocuments])

  useEffect(() => {
    return () => {
      requestIdRef.current += 1
    }
  }, [])

  const value = useMemo(
    () => ({
      updateFilter,
      applyFilters,
      clearFilters,
      setDataDocuments,
      setLoadingData,
      getDataDocuments,
      searchDocuments,
      setFilters,
      setIsMoreData,
      filters,
      isMoreData,
      loadingData: !enabled || loadingData,
      dataDocuments,
      setErrorFormat,
      errorFormat,
      totalPages,
      totalRecords,
      setTotalPages,
      setTotalRecords,
      pageWindowFirst,
      pageWindowLast,
      setPageWindowFirst,
      setPageWindowLast,
    }),
    [
      updateFilter,
      applyFilters,
      clearFilters,
      getDataDocuments,
      searchDocuments,
      filters,
      isMoreData,
      loadingData,
      enabled,
      dataDocuments,
      errorFormat,
      totalPages,
      totalRecords,
      pageWindowFirst,
      pageWindowLast,
    ]
  )

  return (
    <SearchSalesDocumentsContext.Provider value={value}>
      {children}
    </SearchSalesDocumentsContext.Provider>
  )
}

export function useSearchSalesDocumentsContext() {
  const context = useContext(SearchSalesDocumentsContext)
  if (context === undefined) {
    throw new Error(
      'useSearchSalesDocumentsContext must be used within a SearchSalesDocumentsProvider'
    )
  }
  return context
}
